// WithdrawModal.js
import React, { useState, useEffect } from 'react';
import ReactDOM from 'react-dom';
import styled from 'styled-components';
import { APP_CONFIG } from '../config/app';
import { useUserData } from '../contexts/UserDataContext'; // To potentially get balance

const ModalOverlay = styled.div`
  position: fixed !important;
  top: 0 !important;
  left: 0 !important;
  right: 0 !important;
  bottom: 0 !important;
  background-color: rgba(0, 0, 0, 0.85) !important;
  display: flex !important;
  align-items: center !important;
  justify-content: center !important;
  z-index: 999998 !important; /* Slightly lower than daily login if they could overlap, but still very high */
  width: 100vw !important;
  height: 100vh !important;
  animation: fadeIn 0.2s ease-in-out !important;
  box-sizing: border-box !important;
  margin: 0 !important;
  padding: 0 !important;
  backdrop-filter: blur(8px) !important;
  -webkit-backdrop-filter: blur(8px) !important;

  @keyframes fadeIn {
    from { opacity: 0; }
    to { opacity: 1; }
  }
`;

const ModalContainer = styled.div`
  background-color: #2C2F33 !important; /* Darker background for the modal content */
  width: calc(100% - 40px) !important;
  max-width: 400px !important;
  border-radius: 16px !important;
  padding: 20px !important;
  display: flex !important;
  flex-direction: column !important;
  align-items: center !important;
  position: relative !important;
  animation: slideIn 0.3s ease-in-out !important;
  z-index: 999999 !important;
  box-sizing: border-box !important;
  box-shadow: 0 8px 24px rgba(0,0,0,0.3) !important;

  @keyframes slideIn {
    from { transform: translateY(20px) scale(0.95); opacity: 0.8; }
    to { transform: translateY(0) scale(1); opacity: 1; }
  }
`;

const HeaderSection = styled.div`
  display: flex;
  justify-content: space-between; /* Align title left, close button right */
  align-items: center;
  width: 100%;
  margin-bottom: 20px;
`;

const ModalTitle = styled.h2`
  font-size: 24px;
  color: white;
  text-align: left;
  font-weight: 600;
  margin: 0;
  padding: 0;
  line-height: 1.2;
`;

const CloseButton = styled.button`
  background: none;
  border: none;
  color: #AEAEAE; /* Lighter gray for close button */
  font-size: 30px; /* Adjust size if needed */
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 0;
  opacity: 0.8;
  transition: color 0.2s ease;

  &:hover {
    color: white;
  }
`;

const InputGroup = styled.div`
  width: 100%;
  margin-bottom: 15px;
`;

const Label = styled.label`
  font-size: 14px;
  color: #B0B0B0; /* Light gray for label */
  display: block;
  margin-bottom: 8px;
  text-align: left;
`;

const Input = styled.input`
  width: 100%;
  padding: 12px 15px;
  border-radius: 8px;
  border: 1px solid #4A4A4A; /* Darker border */
  background-color: #3A3A3A; /* Dark input background */
  color: white;
  font-size: 16px;
  box-sizing: border-box;
  transition: border-color 0.2s ease;

  &:focus {
    outline: none;
    border-color: #7289DA; /* Accent color on focus */
  }

  &::placeholder {
    color: #787878;
  }
`;

const BalanceInfo = styled.div`
  font-size: 12px;
  color: #AAAAAA;
  text-align: right;
  width: 100%;
  margin-top: -10px;
  margin-bottom: 15px;
`;

const ButtonGroup = styled.div`
  display: flex;
  gap: 10px;
  width: 100%;
  margin-top: 10px;
`;

const ActionButton = styled.button`
  flex: 1;
  padding: 12px 0;
  border-radius: 8px;
  font-size: 16px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s ease;
  border: none;

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

const ConfirmButton = styled(ActionButton)`
  background-color: #7289DA; /* Accent color */
  color: white;

  &:hover:not(:disabled) {
    background-color: #5f73bc;
  }
`;

const CancelButton = styled(ActionButton)`
  background-color: #4A4A4A; /* Dark gray for cancel */
  color: #DDDDDD;

  &:hover:not(:disabled) {
    background-color: #5A5A5A;
  }
`;

const ErrorMessage = styled.p`
  color: #FF6B6B; /* Red for errors */
  font-size: 13px;
  text-align: center;
  margin-top: 10px;
  min-height: 18px; /* Reserve space for error message */
`;

const WithdrawModal = ({ isOpen, onClose, onConfirmWithdraw }) => {
  const [amount, setAmount] = useState('');
  const [error, setError] = useState('');
  const { userData } = useUserData(); // Get user data for balance

  const availableBalance = userData?.balance || 0;

  // Portal root management
  const portalRootRef = React.useRef(document.getElementById('modal-root'));
  if (!portalRootRef.current) {
    portalRootRef.current = document.createElement('div');
    portalRootRef.current.id = 'modal-root';
  }

  useEffect(() => {
    const portalRoot = portalRootRef.current;
    if (isOpen) {
      document.body.appendChild(portalRoot);
      document.body.classList.add('modal-open');
      // Hide other fixed elements (consistent with DailyLoginModal)
      document.querySelectorAll('header, footer, nav, .HeaderWrapper, .CoinInfoContainer').forEach(elem => {
        if (elem) elem.style.display = 'none';
      });
      document.body.style.overflow = 'hidden';
      if (document.documentElement) document.documentElement.style.overflow = 'hidden';


    } else {
      document.body.classList.remove('modal-open');
      document.querySelectorAll('header, footer, nav, .HeaderWrapper, .CoinInfoContainer').forEach(elem => {
        if (elem) elem.style.display = '';
      });
      document.body.style.overflow = '';
      if (document.documentElement) document.documentElement.style.overflow = '';
      // Consider removing portalRoot from body if it's empty and no other modals use it,
      // but for simplicity, we can leave it if multiple modals might reuse it.
      // However, to match DailyLoginModal behavior more closely:
      if (portalRoot.parentElement && portalRoot.childElementCount === 0) {
         // portalRoot.parentElement.removeChild(portalRoot);
      }
    }
    // Ensure portalRoot is added to body if not already for the initial open
    if (isOpen && !portalRoot.parentElement) {
        document.body.appendChild(portalRoot);
    }


    return () => {
      // Cleanup on unmount if modal was open
      if (isOpen) {
        document.body.classList.remove('modal-open');
        document.querySelectorAll('header, footer, nav, .HeaderWrapper, .CoinInfoContainer').forEach(elem => {
          if (elem) elem.style.display = '';
        });
        document.body.style.overflow = '';
        if (document.documentElement) document.documentElement.style.overflow = '';
      }
      // Attempt to remove child only if it's still parented by body and modal is closing
      // This check is a bit more robust.
      if (!isOpen && portalRoot.parentElement === document.body && portalRoot.childElementCount === 0) {
        // document.body.removeChild(portalRoot);
      }
    };
  }, [isOpen]);


  if (!isOpen) return null;

  const handleAmountChange = (e) => {
    const value = e.target.value;
    // Allow only numbers and one decimal point
    if (/^\d*\.?\d*$/.test(value)) {
      setAmount(value);
      const numericValue = parseFloat(value);
      if (numericValue > availableBalance) {
        setError('Amount exceeds available balance.');
      } else if (numericValue <= 0) {
        setError('Amount must be positive.');
      } else if (numericValue < APP_CONFIG.MINIMUM_WITHDRAWAL_AMOUNT_SECONDARY_COIN) {
        setError(`Minimum withdrawal amount is ${APP_CONFIG.MINIMUM_WITHDRAWAL_AMOUNT_SECONDARY_COIN} ${APP_CONFIG.SECONDARY_COIN_SYMBOL}.`);
      } else {
        setError('');
      }
    }
  };

  const handleConfirm = () => {
    const numericAmount = parseFloat(amount);
    // Basic validation (already covered by handleAmountChange and canConfirm logic, but good for safety)
    if (isNaN(numericAmount) || numericAmount <= 0) {
      setError('Please enter a valid positive amount.');
      return;
    }
    if (numericAmount > availableBalance) {
      setError('Withdrawal amount cannot exceed your balance.');
      return;
    }
    if (numericAmount < APP_CONFIG.MINIMUM_WITHDRAWAL_AMOUNT_SECONDARY_COIN) {
      setError(`Minimum withdrawal amount is ${APP_CONFIG.MINIMUM_WITHDRAWAL_AMOUNT_SECONDARY_COIN} ${APP_CONFIG.SECONDARY_COIN_SYMBOL}.`);
      return;
    }
    setError('');
    onConfirmWithdraw(numericAmount); // Pass the numeric amount
    setAmount(''); // Reset amount after confirmation
  };

  const handleClose = () => {
    setAmount('');
    setError('');
    onClose();
  };

  const numericAmount = parseFloat(amount);
  const canConfirm =
    !isNaN(numericAmount) &&
    numericAmount > 0 &&
    numericAmount <= availableBalance &&
    numericAmount >= APP_CONFIG.MINIMUM_WITHDRAWAL_AMOUNT_SECONDARY_COIN &&
    !error; // Ensure no other errors are present

  return ReactDOM.createPortal(
    <ModalOverlay onClick={handleClose}>
      <ModalContainer onClick={e => e.stopPropagation()}>
        <HeaderSection>
          <ModalTitle>Withdraw {APP_CONFIG.SECONDARY_COIN_SYMBOL}</ModalTitle>
          <CloseButton onClick={handleClose}>×</CloseButton>
        </HeaderSection>

        <InputGroup>
          <Label htmlFor="withdraw-amount">Amount to Withdraw</Label>
          <Input
            type="text" // Using text to manage decimal input better with regex
            id="withdraw-amount"
            placeholder={`e.g., 100.00 ${APP_CONFIG.SECONDARY_COIN_SYMBOL}`}
            value={amount}
            onChange={handleAmountChange}
            autoFocus
          />
        </InputGroup>
        <BalanceInfo>
          Available: {availableBalance.toFixed(APP_CONFIG.DECIMALS_SECONDARY_COIN)} {APP_CONFIG.SECONDARY_COIN_SYMBOL}
        </BalanceInfo>

        {error && <ErrorMessage>{error}</ErrorMessage>}
        {!error && <ErrorMessage> </ErrorMessage>} {/* Placeholder for consistent height */}

        <ButtonGroup>
          <CancelButton onClick={handleClose}>Cancel</CancelButton>
          <ConfirmButton onClick={handleConfirm} disabled={!canConfirm}>
            Confirm Withdraw
          </ConfirmButton>
        </ButtonGroup>
      </ModalContainer>
    </ModalOverlay>,
    portalRootRef.current
  );
};

export default WithdrawModal;
